/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component. Matches the same exemption on use-tree-table.ts. */
import type { RowData } from '@tanstack/react-table';
import * as React from 'react';

import {
  logTreeTableDevWarnings,
  type TreeTableDevWarningsOptions,
} from './log-tree-table-dev-warnings.js';

// Fires once per configuration change instead of on every render (sort/filter/expand/any parent
// re-render): logTreeTableDevWarnings is a dev-only console warning, and calling it in the render
// body meant a misconfigured prop logged dozens of times per second during active search (double
// that in React StrictMode), burying the one warning a consumer actually needs to see.
export function useTreeTableDevWarnings<TData extends RowData>({
  table,
  indentColumnId,
  searchColumn,
  manualFiltering,
  onSearchChange,
  onSelectRow,
  onRowClick,
}: Readonly<TreeTableDevWarningsOptions<TData>>): void {
  React.useEffect(() => {
    logTreeTableDevWarnings({
      table,
      indentColumnId,
      searchColumn,
      manualFiltering,
      onSearchChange,
      onSelectRow,
      onRowClick,
    });
    // table deliberately excluded: TanStack's useTable returns a new wrapper reference on every
    // render here, not a value memoized across unrelated state changes (sorting, filtering,
    // expanding) -- including it would re-fire this effect on every one of those and reproduce the
    // exact per-render spam this effect exists to prevent. The warnings this call can produce only
    // depend on the props listed below, not on which table snapshot happens to be current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indentColumnId, searchColumn, manualFiltering, onSearchChange, onSelectRow, onRowClick]);
}
